package co.maytes.standupbot.dashboard.components.marketing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.observeOnce
import co.maytes.standupbot.dashboard.components.motion.prefersReducedMotion
import co.maytes.standupbot.dashboard.components.widgets.SectionStatus
import co.maytes.standupbot.dashboard.components.widgets.SlackMessagePreview
import co.maytes.standupbot.dashboard.styles.Marketing
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val FRAME_MS = 33L
private const val NOT_STARTED_MS = -1L

@Composable
fun StandupDemo() {
    var started by remember { mutableStateOf(false) }
    var run by remember { mutableStateOf(0) }
    var elapsedMs by remember { mutableStateOf(NOT_STARTED_MS) }
    var userStatus by remember { mutableStateOf<SectionStatus?>(null) }

    LaunchedEffect(started, run) {
        if (!started) return@LaunchedEffect
        if (prefersReducedMotion()) {
            elapsedMs = StandupDemoScript.durationMs
            return@LaunchedEffect
        }
        val startedAt = window.performance.now()
        elapsedMs = 0L
        while (elapsedMs < StandupDemoScript.durationMs) {
            delay(FRAME_MS)
            elapsedMs = (window.performance.now() - startedAt).toLong().coerceAtMost(StandupDemoScript.durationMs)
        }
    }

    val frame = StandupDemoScript.frame(elapsedMs)
    val updates = StandupDemoScript.displayedUpdates(frame, userStatus)

    Div(
        attrs = {
            classes("mk-stage")
            ref { element ->
                val stop = observeOnce(element, Marketing.STAGE_THRESHOLD) { started = true }
                onDispose { stop() }
            }
        },
    ) {
        Div(attrs = { classes("mk-steps"); attr("aria-hidden", "true") }) {
            frame.steps.forEach { step ->
                Div(attrs = { classes("mk-step", "mk-step--${step.state.name.lowercase()}") }) {
                    Span(attrs = { classes("mk-step-dot") })
                    Span { Text(step.label) }
                }
            }
        }

        Div(attrs = { classes("mk-window-stack") }) {
            Div(attrs = { classes("mk-window-sizer"); attr("aria-hidden", "true") }) {
                SlackMessagePreview(updates = StandupDemoScript.completeUpdates())
            }
            Div(
                attrs = {
                    classes("mk-window")
                    if (!frame.windowLive) classes("mk-window--pending")
                    if (!frame.actionsVisible) classes("mk-window--typing")
                },
            ) {
                SlackMessagePreview(
                    updates = updates,
                    ackPressed = frame.ackPressed,
                    onAck = { userStatus = SectionStatus.Approved },
                    onEdit = { userStatus = SectionStatus.Edited },
                    onSkip = { userStatus = SectionStatus.SkippedToday },
                )
            }
        }

        Div(attrs = { classes("mk-stage-foot") }) {
            Span { Text("Sample standup. Names and ticket IDs are made up.") }
            Button(
                attrs = Modifier
                    .classNames("mk-textbtn")
                    .onClick {
                        userStatus = null
                        elapsedMs = 0L
                        run += 1
                    }
                    .toAttrs {
                        type(ButtonType.Button)
                        if (!frame.finished) attr("disabled", "")
                    },
            ) { Text("Replay") }
        }
    }
}
